import { Box, Typography } from '@mui/material';
import React, { useRef } from 'react';
import zeplinColor from '../../../../theme/Color';
import { LightTooltip } from '../../CustomTooltip/CustomTooltip';
import style from '../CustomViewTask.module.css';

interface TaskAttachmentsProps {
  files: (File | string)[];
  onFileSelect?: (files: File[]) => void;
  onFileDelete?: (index: number) => void;
}

export const TaskAttachments: React.FC<TaskAttachmentsProps> = ({
  files,
  onFileSelect,
  onFileDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInputClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && onFileSelect) {
      const selectedFiles = Array.from(event.target.files);
      onFileSelect(selectedFiles);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getFileIcon = (file: File | string) => {
    if (file instanceof File) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      return `/assets/${extension}.svg`;
    } else {
      const extension = file.toString().split('.').pop()?.toLowerCase();
      return `/assets/${extension}.svg`;
    }
  };

  const getFileName = (file: File | string) => {
    if (file instanceof File) {
      return file.name;
    } else {
      const filePath = file.toString() || '';
      if (filePath.includes('|')) {
        const s3Key = filePath.split('|')[1];
        const parts = s3Key.split('/');
        return parts[parts.length - 1] || s3Key;
      } else {
        const parts = filePath.split('/');
        return parts[parts.length - 1] || filePath;
      }
    }
  };

  const handleDownload = async (file: File | string) => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      const filePath = file.toString();
      let fileUrl: string;

      if (filePath.includes('|')) {
        const [baseUrl, s3Key] = filePath.split('|');
        fileUrl = `${baseUrl}${s3Key}`;
      } else {
        fileUrl = filePath.startsWith('http')
          ? filePath
          : `https://huddle-users-dev.s3.us-east-1.amazonaws.com/${filePath}`;
      }

      try {
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = getFileName(file);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        window.open(fileUrl, '_blank');
      }
    }
  };

  const getFileSize = (file: File | string) => {
    if (file instanceof File) {
      return file?.size ? `${file.size / 10000}MB` : '';
    }
    return ''; // URL strings don't have size info
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '10px', xl: '16px' },
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
          <img
            className={style['field-icon']}
            src={'/assets/attachment-view.png'}
            alt='logo'
            style={{ objectFit: 'fill' }}
          />
          <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
            Attachment
          </Typography>
        </Box>
        {onFileSelect && (
          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={handleFileInputClick}
          >
            <input
              ref={fileInputRef}
              type='file'
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <img
              width={'24px'}
              height={'24px'}
              src={'/assets/upload-blue.svg'}
              alt='upload'
              style={{ objectFit: 'fill' }}
            />
            <Typography variant='buttonMedium' color={zeplinColor.Brand1}>
              Select File
            </Typography>
          </Box>
        )}
      </Box>
      {files?.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '10px', xl: '16px' } }}>
          {files.map((file, index) => {
            const fileName = getFileName(file);
            const fileIcon = getFileIcon(file);
            const fileSize = getFileSize(file);

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                }}
              >
                <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <img
                    className={style['file-icon']}
                    src={fileIcon}
                    alt='file-type'
                    style={{ objectFit: 'fill' }}
                  />
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                    <LightTooltip
                      title={
                        <Typography variant='body3' color={zeplinColor.Neutral1}>
                          {fileName}
                        </Typography>
                      }
                      placement='bottom-end'
                      style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                    >
                      <Typography variant='responsiveBody2'>
                        {fileName.slice(0, 40)}
                        {fileName.length > 40 && '...'}
                      </Typography>
                    </LightTooltip>
                    <Typography variant='responsiveLabel1'>{fileSize}</Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <img
                    className={style['icon']}
                    src={'/assets/download.svg'}
                    alt='download'
                    style={{ objectFit: 'fill', cursor: 'pointer' }}
                    onClick={() => handleDownload(file)}
                  />
                  {onFileDelete && (
                    <img
                      className={style['icon']}
                      src={'/assets/delete-blue.svg'}
                      alt='delete'
                      style={{ objectFit: 'fill', cursor: 'pointer' }}
                      onClick={() => onFileDelete(index)}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Typography variant='responsiveBody3' color={zeplinColor.Neutral8} sx={{ padding: '12px' }}>
          No attachments
        </Typography>
      )}
    </Box>
  );
};
